import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { getCachedImageUrl } from '@/lib/image-cache';
import { cn } from '@/lib/utils';
import { ExternalLink, Loader2, MapPin } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface AddressMapCardProps {
    address: string;
    lat?: number | null;
    lng?: number | null;
    /** Tailwind height for the map surface. */
    heightClassName?: string;
    className?: string;
}

/**
 * A map and nothing else: rounded, self-contained, with the "Open in Maps" link
 * floating over its top-right corner instead of a card header and footer wrapped
 * around it. The header rendered an empty title and the footer held only that one
 * link, so both were chrome the map did not need.
 */
export function AddressMapCard({ address, lat, lng, heightClassName = 'h-[260px] sm:h-[320px]', className }: AddressMapCardProps) {
    const { t } = useTranslations();
    const [cachedImageUrl, setCachedImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const blobUrlRef = useRef<string | null>(null);
    const previousAddressKeyRef = useRef<string | null>(null);

    const mapData = useMemo(() => {
        const env = import.meta.env as unknown as Record<string, string | undefined>;
        const apiKey = env.VITE_GOOGLE_MAPS_API_KEY || env.GOOGLE_MAPS_API_KEY;

        if (!apiKey) return null;

        const hasCoordinates = Boolean(lat && lng);
        const trimmedAddress = address?.trim() ?? '';

        if (!hasCoordinates && !trimmedAddress) return null;

        // scale=2 keeps the pin and street labels crisp on retina displays.
        const base = 'https://maps.googleapis.com/maps/api/staticmap?zoom=15&size=640x360&scale=2&maptype=roadmap';

        if (hasCoordinates) {
            const point = `${lat},${lng}`;

            return {
                addressKey: `latlng:${point}`,
                staticMapUrl: `${base}&center=${point}&markers=color:red|${point}&key=${apiKey}`,
                googleMapsLink: `https://www.google.com/maps?q=${point}`,
            };
        }

        const encoded = encodeURIComponent(trimmedAddress);

        return {
            addressKey: `addr:${trimmedAddress}`,
            staticMapUrl: `${base}&center=${encoded}&markers=color:red|${encoded}&key=${apiKey}`,
            googleMapsLink: `https://www.google.com/maps/search/?api=1&query=${encoded}`,
        };
    }, [address, lat, lng]);

    // The static map for a given point never changes; only refetch when it moves.
    useEffect(() => {
        if (!mapData?.staticMapUrl) {
            setCachedImageUrl(null);
            setIsLoading(false);
            previousAddressKeyRef.current = null;

            return;
        }

        if (previousAddressKeyRef.current === mapData.addressKey) {
            return;
        }

        let isMounted = true;
        setIsLoading(true);

        if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        getCachedImageUrl(mapData.staticMapUrl)
            .then((url) => {
                if (!isMounted) {
                    return;
                }

                blobUrlRef.current = url.startsWith('blob:') ? url : null;
                setCachedImageUrl(url);
                setIsLoading(false);
                previousAddressKeyRef.current = mapData.addressKey;
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                // Fall back to the direct URL; the browser can still fetch it.
                blobUrlRef.current = null;
                setCachedImageUrl(mapData.staticMapUrl);
                setIsLoading(false);
                previousAddressKeyRef.current = mapData.addressKey;
            });

        return () => {
            isMounted = false;

            if (blobUrlRef.current && blobUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [mapData?.staticMapUrl, mapData?.addressKey]);

    if (!mapData) {
        return (
            <div
                className={cn(
                    'bg-muted text-muted-foreground flex flex-col items-center justify-center gap-1.5 rounded-xl border',
                    heightClassName,
                    className,
                )}
            >
                <MapPin className="h-6 w-6 opacity-50" />
                <p className="text-sm">{t('common.map.no_location', 'No location data available')}</p>
            </div>
        );
    }

    return (
        <div className={cn('bg-muted relative overflow-hidden rounded-xl border', heightClassName, className)}>
            {cachedImageUrl ? (
                <img
                    src={cachedImageUrl}
                    alt={t('common.map.alt', 'Location map')}
                    className="h-full w-full object-cover"
                    onError={() => setCachedImageUrl(mapData.staticMapUrl)}
                />
            ) : null}

            {isLoading ? (
                <div className="bg-muted/60 absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin opacity-50" />
                </div>
            ) : null}

            {/* Floats over the map rather than sitting in a footer below it. */}
            <Button
                variant="secondary"
                size="sm"
                asChild
                className="bg-background/90 hover:bg-background absolute top-2 right-2 h-8 gap-1.5 shadow-sm backdrop-blur"
            >
                <a href={mapData.googleMapsLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="text-xs">{t('common.map.open_in_maps', 'Open in Maps')}</span>
                </a>
            </Button>
        </div>
    );
}
